import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MapPin, Phone, Star, CheckCircle, Calendar, Clock, Award, 
  Languages, ArrowLeft, MessageCircle, Share2, Heart,
  ChevronRight, User, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PanditDetailPage({ user }) {
  const { panditId } = useParams();
  const navigate = useNavigate();
  
  const [pandit, setPandit] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [bookingData, setBookingData] = useState({
    pooja_type: '',
    booking_date: '',
    booking_time: '',
    devotee_name: user?.name || '',
    devotee_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    special_requirements: '',
    agreed_fee: 0
  });

  useEffect(() => {
    fetchPandit();
    fetchReviews();
  }, [panditId]);

  const fetchPandit = async () => {
    try {
      const res = await axios.get(`${API}/pandit/profile/${panditId}`);
      setPandit(res.data);
      setBookingData(prev => ({
        ...prev,
        agreed_fee: res.data.fees_per_puja
      }));
    } catch (error) {
      toast.error('Failed to load pandit profile');
      navigate('/pandit');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/pandit/${panditId}/reviews`);
      setReviews(res.data);
    } catch (error) {
      console.error('Failed to load reviews');
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to book');
      navigate('/auth');
      return;
    }

    // Validation
    if (!bookingData.pooja_type || !bookingData.booking_date || !bookingData.devotee_name || 
        !bookingData.devotee_phone || !bookingData.address || !bookingData.city || !bookingData.state) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Step 1: Create booking
      const bookingRes = await axios.post(`${API}/pandit/booking`, {
        pandit_id: panditId,
        ...bookingData
      }, { headers });

      const bookingId = bookingRes.data.booking_id;

      // Step 2: Create payment order
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        booking_id: bookingId
      }, { headers });

      const orderData = orderRes.data;

      // Step 3: Open Razorpay checkout
      if (orderData.key_id && window.Razorpay) {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount_paise,
          currency: orderData.currency,
          name: 'DharmBook',
          description: `${bookingData.pooja_type} - ${pandit?.name || 'Pandit Ji'}`,
          order_id: orderData.order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/payments/verify`, {
                booking_id: bookingId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }, { headers });
              toast.success('Payment successful! Booking confirmed.');
              setShowBooking(false);
              resetBookingForm();
            } catch {
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: orderData.prefill?.name || user?.name || '',
            email: orderData.prefill?.email || user?.email || '',
            contact: bookingData.devotee_phone,
          },
          theme: { color: '#FF9933' },
          modal: {
            ondismiss: () => {
              toast.info('Payment cancelled. Your booking is saved — you can pay later from My Bookings.');
            }
          }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // Razorpay not configured — booking created without payment
        toast.success('Booking request sent! Payment will be collected later.');
        setShowBooking(false);
        resetBookingForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send booking request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetBookingForm = () => {
    setBookingData({
      pooja_type: '',
      booking_date: '',
      booking_time: '',
      devotee_name: user?.name || '',
      devotee_phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      special_requirements: '',
      agreed_fee: pandit?.fees_per_puja || 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-3 animate-pulse">ॐ</div>
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!pandit) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Pandit not found</p>
        <Button onClick={() => navigate('/pandit')} className="mt-4">
          Back to Search
        </Button>
      </div>
    );
  }

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pb-24 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/pandit')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Search</span>
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg -mt-2 overflow-hidden">
          {/* Cover */}
          <div className="h-32 bg-gradient-to-r from-orange-100 to-orange-50" />
          
          {/* Profile Info */}
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-12">
              {/* Avatar */}
              <div className="relative">
                <img
                  src={pandit.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(pandit.name)}&background=FF9933&color=fff&size=150`}
                  alt={pandit.name}
                  className="w-28 h-28 rounded-2xl object-cover border-4 border-white shadow-md"
                />
                {pandit.verified && (
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-3 border-white">
                    <CheckCircle className="w-5 h-5 text-white" fill="white" stroke="green" />
                  </div>
                )}
              </div>

              {/* Name & Rating */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
                    {pandit.name}
                  </h1>
                  {pandit.verified && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-3 mt-2 text-gray-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{pandit.city}, {pandit.state}</span>
                  </div>
                  {pandit.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold">{pandit.rating.toFixed(1)}</span>
                      <span className="text-gray-400">({pandit.reviews_count} reviews)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="rounded-full w-10 h-10"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
                <Button
                  onClick={() => setShowBooking(true)}
                  className="rounded-full bg-[#FF9933] hover:bg-[#E68A00] px-6"
                  data-testid="book-now-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <Clock className="w-6 h-6 mx-auto text-[#FF9933] mb-2" />
            <div className="text-2xl font-bold text-gray-900">{pandit.experience_years}</div>
            <div className="text-xs text-gray-500">Years Experience</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <FileText className="w-6 h-6 mx-auto text-[#FF9933] mb-2" />
            <div className="text-2xl font-bold text-gray-900">{pandit.completed_bookings || 0}</div>
            <div className="text-xs text-gray-500">Poojas Done</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <Star className="w-6 h-6 mx-auto text-[#FF9933] mb-2" />
            <div className="text-2xl font-bold text-gray-900">{pandit.rating > 0 ? pandit.rating.toFixed(1) : 'New'}</div>
            <div className="text-xs text-gray-500">Rating</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <MessageCircle className="w-6 h-6 mx-auto text-[#FF9933] mb-2" />
            <div className="text-2xl font-bold text-gray-900">{pandit.reviews_count || 0}</div>
            <div className="text-xs text-gray-500">Reviews</div>
          </div>
        </div>

        {/* Details Sections */}
        <div className="mt-4 space-y-4">
          {/* Fees */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">Fees</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-[#FF9933]">₹{pandit.fees_range_min}</span>
              <span className="text-gray-400">-</span>
              <span className="text-3xl font-bold text-[#FF9933]">₹{pandit.fees_range_max}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Starting from ₹{pandit.fees_per_puja} per pooja</p>
          </div>

          {/* Specializations */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">Pooja Services</h3>
            <div className="flex flex-wrap gap-2">
              {pandit.puja_types.map((puja, i) => (
                <Badge key={i} variant="secondary" className="px-3 py-1.5 bg-orange-50 text-orange-700 hover:bg-orange-100">
                  {puja}
                </Badge>
              ))}
            </div>
            {pandit.specialization && (
              <p className="mt-3 text-gray-600 text-sm">{pandit.specialization}</p>
            )}
          </div>

          {/* Qualification */}
          {(pandit.qualification || pandit.gurukul_name) && (
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Qualification</h3>
              {pandit.qualification && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Award className="w-5 h-5 text-[#FF9933]" />
                  <span>{pandit.qualification}</span>
                </div>
              )}
              {pandit.gurukul_name && (
                <p className="text-gray-500 text-sm mt-1 ml-7">{pandit.gurukul_name}</p>
              )}
            </div>
          )}

          {/* Languages */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">Languages</h3>
            <div className="flex flex-wrap gap-2">
              {pandit.languages.map((lang, i) => (
                <Badge key={i} variant="outline" className="px-3 py-1.5">
                  {lang}
                </Badge>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">Contact</h3>
            <div className="space-y-2">
              <a href={`tel:${pandit.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-[#FF9933]">
                <Phone className="w-5 h-5" />
                <span>{pandit.phone}</span>
              </a>
              {pandit.whatsapp && (
                <a 
                  href={`https://wa.me/${pandit.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-600 hover:text-green-700"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
              )}
            </div>
          </div>

          {/* Reviews Section */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Reviews</h3>
              {reviews.length > 0 && (
                <span className="text-sm text-gray-500">{reviews.length} reviews</span>
              )}
            </div>
            
            {reviews.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No reviews yet</p>
            ) : (
              <div className="space-y-4">
                {reviews.slice(0, 5).map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-start gap-3">
                      <img
                        src={review.reviewer_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.reviewer_name)}&background=eee&size=40`}
                        alt={review.reviewer_name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{review.reviewer_name}</span>
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-3.5 h-3.5 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">{review.pooja_type}</p>
                        {review.review_text && (
                          <p className="text-gray-600 text-sm">{review.review_text}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">Book Pooja with {pandit.name}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleBooking} className="space-y-4 mt-2">
            {/* Pooja Type */}
            <div>
              <Label htmlFor="pooja_type">Pooja Type *</Label>
              <Select 
                value={bookingData.pooja_type} 
                onValueChange={(v) => setBookingData({ ...bookingData, pooja_type: v })}
              >
                <SelectTrigger className="rounded-xl mt-1" data-testid="booking-pooja-select">
                  <SelectValue placeholder="Select Pooja" />
                </SelectTrigger>
                <SelectContent>
                  {pandit.puja_types.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="booking_date">Date *</Label>
                <Input
                  id="booking_date"
                  type="date"
                  min={minDate}
                  value={bookingData.booking_date}
                  onChange={(e) => setBookingData({ ...bookingData, booking_date: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-date-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="booking_time">Time (Optional)</Label>
                <Input
                  id="booking_time"
                  type="time"
                  value={bookingData.booking_time}
                  onChange={(e) => setBookingData({ ...bookingData, booking_time: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-time-input"
                />
              </div>
            </div>

            {/* Personal Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="devotee_name">Your Name *</Label>
                <Input
                  id="devotee_name"
                  value={bookingData.devotee_name}
                  onChange={(e) => setBookingData({ ...bookingData, devotee_name: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-name-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="devotee_phone">Phone *</Label>
                <Input
                  id="devotee_phone"
                  type="tel"
                  value={bookingData.devotee_phone}
                  onChange={(e) => setBookingData({ ...bookingData, devotee_phone: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-phone-input"
                  required
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={bookingData.address}
                onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })}
                placeholder="House/Flat, Street, Landmark"
                className="rounded-xl mt-1"
                data-testid="booking-address-input"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={bookingData.city}
                  onChange={(e) => setBookingData({ ...bookingData, city: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-city-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={bookingData.state}
                  onChange={(e) => setBookingData({ ...bookingData, state: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-state-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="pincode">Pincode</Label>
                <Input
                  id="pincode"
                  value={bookingData.pincode}
                  onChange={(e) => setBookingData({ ...bookingData, pincode: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="booking-pincode-input"
                />
              </div>
            </div>

            {/* Special Requirements */}
            <div>
              <Label htmlFor="special_requirements">Special Requirements</Label>
              <Textarea
                id="special_requirements"
                value={bookingData.special_requirements}
                onChange={(e) => setBookingData({ ...bookingData, special_requirements: e.target.value })}
                placeholder="Any special requirements or notes for the pandit..."
                rows={3}
                className="rounded-xl mt-1"
                data-testid="booking-requirements-input"
              />
            </div>

            {/* Fee & Payment Breakdown */}
            <div className="bg-orange-50 rounded-xl p-4" data-testid="payment-breakdown">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700 font-medium">Pooja Fee</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">₹</span>
                  <Input
                    type="number"
                    value={bookingData.agreed_fee}
                    onChange={(e) => setBookingData({ ...bookingData, agreed_fee: parseFloat(e.target.value) || 0 })}
                    className="w-24 rounded-lg text-right font-semibold"
                    data-testid="booking-fee-input"
                  />
                </div>
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Platform fee (20%)</span>
                  <span>₹{(bookingData.agreed_fee * 0.2).toFixed(0)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Pandit Ji receives (80%)</span>
                  <span>₹{(bookingData.agreed_fee * 0.8).toFixed(0)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-orange-700 pt-1 border-t border-orange-200 mt-1">
                  <span>Total Payable</span>
                  <span>₹{bookingData.agreed_fee}</span>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                Full refund on cancellation before pooja. Base fee: ₹{pandit.fees_per_puja}
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-[#FF9933] hover:bg-[#E68A00] py-6 text-lg"
              data-testid="submit-booking-btn"
            >
              {submitting ? 'Processing...' : `Book & Pay ₹${bookingData.agreed_fee}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fixed Book Button (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t md:hidden z-50">
        <Button
          onClick={() => setShowBooking(true)}
          className="w-full rounded-xl bg-[#FF9933] hover:bg-[#E68A00] py-6 text-lg"
          data-testid="mobile-book-btn"
        >
          <Calendar className="w-5 h-5 mr-2" />
          Book Pooja - ₹{pandit.fees_per_puja}
        </Button>
      </div>
    </div>
  );
}
